import { FC } from 'react';
import {
  Avatar,
  Circle,
  Divider,
  Flex,
  HStack,
  Spacer,
  VStack,
} from '@chakra-ui/react';
import { printFormat as formatIban } from 'iban';

import { BankTransferRecipient } from '__generated__/GQL';
import SunshineIcon from 'components/_core/SunshineIcon';
import Text from 'components/_core/Text';

import { BankTransferInputAtInternationalTransferRecap } from '../../../../contexts';
import BankAccountDetails from './components/BankAccountDetails';
import { PartialBankAccount } from './types';

interface BankAccountsDetailsProps {
  emittingBankAccount: PartialBankAccount;
  bankTransferRecipient?: Pick<
    BankTransferRecipient,
    'iban' | 'name' | 'label'
  >;
  internationalBankTransferRecipient?: Pick<
    BankTransferInputAtInternationalTransferRecap['recipient'],
    'accountSummary' | 'fullName' | 'currency'
  >;
  receivingBankAccount?: PartialBankAccount;
}

const BankAccountsDetails: FC<BankAccountsDetailsProps> = ({
  bankTransferRecipient,
  emittingBankAccount,
  internationalBankTransferRecipient,
  receivingBankAccount,
}) => {
  const bankTransferRecipientLabel =
    bankTransferRecipient?.label || bankTransferRecipient?.name;

  return (
    <Flex gap="space-12" justifyContent="space-between">
      <BankAccountDetails bankAccount={emittingBankAccount} />
      <Spacer flex="none" />
      <Flex
        alignItems="center"
        flex="none"
        height="42px"
        justifyContent="center"
        position="relative"
        width="28px"
      >
        <Divider orientation="vertical" position="absolute" />
        <Circle
          backgroundColor="white"
          borderColor="border"
          borderWidth={1}
          position="absolute"
          size="28px"
        >
          <SunshineIcon color="grey.600" name="arrow-right" />
        </Circle>
      </Flex>
      <Spacer flex="none" />

      {bankTransferRecipient ? (
        <HStack flex={1} spacing="space-8">
          <Avatar
            backgroundColor="grey.200"
            borderRadius="radius-12"
            boxSize="40px"
            color="grey.800"
            fontWeight="weight-500"
            name={bankTransferRecipientLabel}
          />
          <VStack alignItems="start" spacing={0}>
            <Text fontWeight="weight-500" variant="primary">
              {bankTransferRecipientLabel}
            </Text>
            <Text size="sm" variant="light" whiteSpace="nowrap">
              {bankTransferRecipient.iban
                ? formatIban(bankTransferRecipient.iban)
                : ''}
            </Text>
          </VStack>
        </HStack>
      ) : null}

      {internationalBankTransferRecipient ? (
        <HStack flex={1} spacing="space-8">
          <Avatar
            backgroundColor="grey.200"
            borderRadius="radius-12"
            boxSize="40px"
            color="grey.800"
            fontWeight="weight-500"
            name={internationalBankTransferRecipient.fullName}
          />
          <VStack alignItems="start" spacing={0}>
            <Text fontWeight="weight-500" variant="primary">
              {internationalBankTransferRecipient.fullName}
            </Text>
            <Text size="sm" variant="light" whiteSpace="nowrap">
              {`${internationalBankTransferRecipient?.currency} · ${internationalBankTransferRecipient?.accountSummary}`}
            </Text>
          </VStack>
        </HStack>
      ) : null}
      {receivingBankAccount ? (
        <BankAccountDetails bankAccount={receivingBankAccount} />
      ) : null}
    </Flex>
  );
};

export default BankAccountsDetails;
